/**
 * Featured items horizontal carousel
 * Shows featured products with navigation arrows
 */
import { memo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui'
import { MenuItem } from '@/types/menu'
import { FeaturedItemCard } from './FeaturedItemCard'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'

interface FeaturedCarouselProps {
  items: MenuItem[]
  onItemClick: (item: MenuItem) => void
}

export const FeaturedCarousel = memo(function FeaturedCarousel({ items, onItemClick }: FeaturedCarouselProps) {
  const { scrollRef, canScrollLeft, canScrollRight, scrollBy } = useHorizontalScroll({
    scrollAmount: 200,
    deps: [items],
  })

  if (items.length === 0) return null

  return (
    <section className="mb-10">
      {/* Header with title and navigation arrows */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Featured Items</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Popular picks from the menu</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            className="rounded-full h-8 w-8"
            onClick={() => scrollBy('left')}
            disabled={!canScrollLeft}
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full h-8 w-8"
            onClick={() => scrollBy('right')}
            disabled={!canScrollRight}
            aria-label="Scroll right"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Scrollable carousel */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide pb-2"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {items.map((item) => (
          <FeaturedItemCard
            key={item.id}
            item={item}
            onItemClick={onItemClick}
          />
        ))}
      </div>
    </section>
  )
})
